// -----------------------------------------------------------------------------
//  Gallery / Project stories
//
//  This file is the single source of truth for the horizontal gallery on the
//  home page AND the individual project story pages at /projects/:slug.
//
//  The shape below (`ProjectStory`) is intentionally close to a Postgres row —
//  scalar fields at the top, JSON-shaped arrays (`chapters`, `techniques`,
//  `images`, `credits`) below. When you connect Supabase later, one table
//  `projects` with a `jsonb` column for each of those arrays maps 1:1 and
//  the frontend won't need to change.
// -----------------------------------------------------------------------------

export interface ProjectChapter {
  eyebrow: string;
  heading: string;
  body: string;
  image: string;
  imageAlt: string;
  alignment: "left" | "right";
}

export interface LightingTechnique {
  code: string;
  title: string;
  detail: string;
  spec: string;
}

export interface ProjectImage {
  src: string;
  alt: string;
  caption: string;
  aspect: "portrait" | "landscape" | "square";
}

export interface ProjectCredit {
  role: string;
  name: string;
}

export interface ProjectStory {
  id: number;
  slug: string;
  label: string;
  subtitle: string;
  title: string;
  location: string;
  year: string;
  discipline: string;
  image: string; // hero card image, shown on the home page strip
  heroImage: string; // larger hero for the story page
  tagline: string;
  intro: string;
  chapters: ProjectChapter[];
  techniques: LightingTechnique[];
  images: ProjectImage[];
  credits: ProjectCredit[];
  pullquote: {
    quote: string;
    attribution: string;
  };
}

export const galleryCards: ProjectStory[] = [
  {
    id: 1,
    slug: "nocturne-lobby",
    label: "01 / Hospitality",
    subtitle: "Hotel Kagome · Kyoto · 2024",
    title: "Nocturne Lobby",
    location: "Kyoto, Japan",
    year: "2024",
    discipline: "Hospitality · Lobby",
    tagline: "A lantern-lit welcome that slows the guest before they even reach the desk.",
    image:
      "https://readdy.ai/api/search-image?query=Warm%20amber%20lantern%20light%20glowing%20softly%20through%20a%20minimalist%20Japanese%20hotel%20lobby%20at%20dusk%2C%20low%20pendant%20lights%20hovering%20over%20dark%20timber%20and%20stone%2C%20quiet%20cinematic%20mood%2C%20editorial%20architectural%20interior%20photography%20with%20deep%20negative%20space%2C%20gentle%20film%20grain%2C%20harmonious%20pastel%20warm%20tones%20on%20matte%20black%20background&width=900&height=1200&seq=lumen-gallery-01&orientation=portrait",
    heroImage:
      "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20editorial%20photograph%20of%20a%20minimalist%20Japanese%20hotel%20lobby%20at%20dusk%2C%20a%20constellation%20of%20warm%20amber%20paper%20lanterns%20suspended%20at%20varied%20heights%20above%20a%20long%20dark%20timber%20reception%20counter%2C%20polished%20black%20stone%20floor%20reflecting%20the%20soft%20glow%2C%20a%20single%20guest%20silhouette%20removing%20a%20coat%2C%20late%20evening%20navy%20blue%20light%20seeping%20through%20paper%20screen%20windows%2C%20quiet%20atmospheric%20haze%2C%20fine%20film%20grain%2C%20refined%20editorial%20interior%20photography%20by%20a%20master%20architectural%20photographer&width=2400&height=1350&seq=nocturne-lobby-hero-01&orientation=landscape",
    intro:
      "Kagome asked for a lobby that felt like exhaling. Not grand, not dramatic — a room where a traveller could set down their case, breathe once, and know they had arrived somewhere quiet. We answered with thirty-two paper lanterns, hand-tuned to 2200K, hung at seven precise heights that trace the walk from door to desk.",
    chapters: [
      {
        eyebrow: "Chapter 01 · The Threshold",
        heading: "Arrival as a lowered voice.",
        body: "The first six lanterns hang low — barely above head height — so the guest ducks slightly, unconsciously, into a quieter register. It's a physical hush before the human one. Behind them, the entry passage stays deliberately dim; the lanterns pull the eye forward without ever demanding it.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20editorial%20photograph%20of%20a%20narrow%20entry%20passage%20in%20a%20minimalist%20Japanese%20hotel%20with%20a%20line%20of%20six%20low%20hanging%20warm%20amber%20paper%20lanterns%20casting%20soft%20pools%20of%20honey%20light%20on%20a%20dark%20timber%20floor%2C%20plaster%20walls%20fading%20into%20shadow%2C%20a%20lone%20figure%20walking%20away%20from%20the%20camera%2C%20quiet%20cinematic%20mood%2C%20fine%20film%20grain%20and%20delicate%20atmospheric%20haze%2C%20muted%20warm%20palette&width=1800&height=1200&seq=nocturne-lobby-ch1&orientation=landscape",
        imageAlt: "Low lanterns along the entry passage",
        alignment: "left",
      },
      {
        eyebrow: "Chapter 02 · The Desk",
        heading: "One warm island in a cool sea.",
        body: "The reception counter itself is lit from below by a hidden 2000K wash — the timber glows the colour of old whisky. Around it, the room stays at 2700K, ten stops darker. The result: the desk is the only warm object in the room, and the eye lands there without being told.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20interior%20photograph%20of%20a%20long%20dark%20timber%20hotel%20reception%20counter%20softly%20glowing%20from%20a%20hidden%20underlight%20in%20warm%20honey%20amber%20tones%2C%20surrounded%20by%20a%20cooler%20dim%20blue%20black%20space%2C%20a%20single%20brass%20table%20lamp%20and%20a%20small%20vase%20of%20dried%20grass%20on%20top%2C%20quiet%20luxury%20hospitality%20photography%2C%20fine%20film%20grain%2C%20subtle%20atmospheric%20depth&width=1800&height=1200&seq=nocturne-lobby-ch2&orientation=landscape",
        imageAlt: "The counter, softly underlit",
        alignment: "right",
      },
      {
        eyebrow: "Chapter 03 · The Lift",
        heading: "A quiet punctuation mark.",
        body: "Between the desk and the lift lobby, a single sconce marks the corner — a small brass half-moon at shoulder height. It's the only unshaded light in the room. Guests pass it, glance at it, and only later realise it was the thing that told them where to turn.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20editorial%20photograph%20of%20a%20single%20small%20brass%20wall%20sconce%20mounted%20at%20shoulder%20height%20on%20a%20warm%20plaster%20wall%20in%20a%20minimalist%20Japanese%20hotel%20corridor%2C%20casting%20a%20soft%20half%20moon%20of%20amber%20light%2C%20dark%20polished%20timber%20floor%20reflecting%20the%20glow%2C%20quiet%20intimate%20architectural%20photography%2C%20fine%20grain%2C%20negative%20space%2C%20refined%20warm%20palette&width=1800&height=1200&seq=nocturne-lobby-ch3&orientation=landscape",
        imageAlt: "Brass half-moon sconce at the lift lobby corner",
        alignment: "left",
      },
    ],
    techniques: [
      {
        code: "T-01",
        title: "Layered lantern grid",
        detail: "32 hand-blown washi lanterns, dimmed individually via DALI, hung on seven precise heights.",
        spec: "2200K · CRI 96 · DALI-2",
      },
      {
        code: "T-02",
        title: "Hidden desk underlight",
        detail: "A concealed LED linear runs the full length of the counter, warming the timber from below.",
        spec: "2000K · 8W/m · dim-to-warm",
      },
      {
        code: "T-03",
        title: "Cool ambient shell",
        detail: "The surrounding room sits at 2700K to make the desk feel like the only warm object in view.",
        spec: "2700K · CRI 95",
      },
      {
        code: "T-04",
        title: "Directional sconce",
        detail: "One brass half-moon at the lift lobby corner. The room's only unshaded light source.",
        spec: "2400K · 3W · hand-patinated brass",
      },
    ],
    images: [
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20close%20up%20detail%20of%20a%20single%20warm%20amber%20washi%20paper%20lantern%20softly%20glowing%20in%20a%20dark%20minimalist%20Japanese%20hotel%20lobby%2C%20subtle%20fibre%20texture%20visible%20in%20the%20paper%2C%20honeyed%20light%20bleeding%20out%20into%20the%20room%2C%20negative%20space%2C%20film%20grain%2C%20quiet%20cinematic%20interior%20photography&width=1200&height=1500&seq=nocturne-lobby-img1&orientation=portrait",
        alt: "Close-up of a single washi lantern",
        caption: "Lantern No. 12 · 2200K",
        aspect: "portrait",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20wide%20interior%20photograph%20of%20a%20minimalist%20Japanese%20hotel%20lobby%20at%20blue%20hour%20with%20a%20full%20constellation%20of%20warm%20amber%20paper%20lanterns%20suspended%20above%20a%20polished%20black%20stone%20floor%2C%20soft%20reflections%2C%20a%20guest%20silhouette%20crossing%20the%20room%2C%20cinematic%20editorial%20architectural%20photography%2C%20fine%20film%20grain%2C%20refined%20mood&width=2000&height=1200&seq=nocturne-lobby-img2&orientation=landscape",
        alt: "Wide view of the full lobby",
        caption: "Wide view · Evening 21:00",
        aspect: "landscape",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20editorial%20photograph%20of%20a%20guest%20hand%20receiving%20a%20small%20brass%20room%20key%20on%20a%20softly%20underlit%20dark%20timber%20hotel%20reception%20counter%2C%20warm%20amber%20glow%2C%20intimate%20quiet%20mood%2C%20shallow%20depth%20of%20field%2C%20fine%20film%20grain%2C%20cinematic%20hospitality%20photography&width=1200&height=1500&seq=nocturne-lobby-img3&orientation=portrait",
        alt: "Detail of the reception moment",
        caption: "Detail · Handover",
        aspect: "portrait",
      },
    ],
    credits: [
      { role: "Lighting Design", name: "Elin Vahr Studio" },
      { role: "Client", name: "Hotel Kagome" },
      { role: "Architecture", name: "Sasaki + Ono Atelier" },
      { role: "Photography", name: "Yuji Watanabe" },
    ],
    pullquote: {
      quote: "You don't remember the lobby. You remember how you felt when you set your bag down.",
      attribution: "Elin Vahr",
    },
  },
  {
    id: 2,
    slug: "the-amber-cellar",
    label: "02 / Hospitality",
    subtitle: "Bar Sølv · Copenhagen · 2023",
    title: "The Amber Cellar",
    location: "Copenhagen, Denmark",
    year: "2023",
    discipline: "Hospitality · Bar",
    tagline: "An underground wine bar lit only by fire, honey and a single arc of hidden LED.",
    image:
      "https://readdy.ai/api/search-image?query=Intimate%20underground%20wine%20bar%20bathed%20in%20golden%20peach%20and%20honey%20candlelight%2C%20arched%20brick%20ceiling%20glowing%20softly%2C%20sculptural%20pendant%20lighting%20reflected%20on%20polished%20dark%20wood%20counter%2C%20quiet%20cinematic%20editorial%20interior%20photography%20with%20dreamy%20warm%20haze%20and%20deep%20negative%20space%2C%20subtle%20film%20grain&width=900&height=1200&seq=lumen-gallery-02&orientation=portrait",
    heroImage:
      "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20photograph%20of%20an%20intimate%20underground%20wine%20cellar%20bar%20in%20Copenhagen%20lit%20entirely%20by%20warm%20candles%20and%20a%20single%20hidden%20honey%20amber%20arc%20of%20LED%20behind%20the%20liquor%20shelf%2C%20arched%20aged%20brick%20ceiling%20glowing%20softly%2C%20polished%20dark%20oak%20counter%20reflecting%20warm%20highlights%2C%20guests%20in%20quiet%20conversation%20along%20the%20bar%2C%20fine%20film%20grain%2C%20atmospheric%20haze%2C%20editorial%20hospitality%20photography%20by%20a%20master%20architectural%20photographer&width=2400&height=1350&seq=amber-cellar-hero&orientation=landscape",
    intro:
      "Bar Sølv sits three floors under a Copenhagen townhouse. The brick barrel vault was built in 1873 and has spent the last century in almost total darkness. We were asked to light it — and to disappear while doing so. Not a single fixture is visible from a seated position. Only candles and their reflections.",
    chapters: [
      {
        eyebrow: "Chapter 01 · Fire First",
        heading: "Candlelight as the base layer.",
        body: "Every seated position has its own beeswax candle, replaced hourly by the staff. This is the room's true illumination — everything else exists only to fill the shadows the candles can't reach.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20editorial%20photograph%20of%20a%20cluster%20of%20warm%20beeswax%20candles%20burning%20on%20a%20polished%20dark%20oak%20bar%20counter%20in%20an%20underground%20wine%20cellar%2C%20soft%20honeyed%20flame%20light%20reflecting%20on%20the%20wood%20and%20glasses%2C%20aged%20brick%20wall%20softly%20lit%20behind%2C%20quiet%20intimate%20mood%2C%20fine%20film%20grain%2C%20shallow%20depth%20of%20field&width=1800&height=1200&seq=amber-cellar-ch1&orientation=landscape",
        imageAlt: "Beeswax candles along the counter",
        alignment: "left",
      },
      {
        eyebrow: "Chapter 02 · The Hidden Arc",
        heading: "One line of light, invisible.",
        body: "A single 4-metre linear LED runs behind the liquor shelf, angled upward at 12°. Guests never see it — only the resulting glow that traces the vault of the ceiling. It's the room's only piece of modern technology, and it looks like memory.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20interior%20photograph%20of%20an%20aged%20brick%20barrel%20vault%20ceiling%20in%20a%20Copenhagen%20wine%20cellar%20softly%20traced%20by%20hidden%20warm%20amber%20uplight%2C%20the%20brick%20texture%20catching%20the%20honey%20glow%2C%20quiet%20negative%20space%2C%20cinematic%20architectural%20photography%2C%20fine%20film%20grain%2C%20subtle%20atmospheric%20haze&width=1800&height=1200&seq=amber-cellar-ch2&orientation=landscape",
        imageAlt: "The brick vault, traced by uplight",
        alignment: "right",
      },
    ],
    techniques: [
      {
        code: "T-01",
        title: "Beeswax at every seat",
        detail: "260 hand-poured beeswax candles per week, changed on the hour by the floor team.",
        spec: "≈1800K flame · replaced hourly",
      },
      {
        code: "T-02",
        title: "Concealed vault uplight",
        detail: "One continuous linear LED, angled 12° up, hidden behind the top shelf of the back bar.",
        spec: "2200K · 6W/m · 12° tilt",
      },
      {
        code: "T-03",
        title: "Anti-glare shelf lip",
        detail: "A hand-turned oak lip on every shelf edge intercepts every possible sightline to the source.",
        spec: "Solid oak · 18mm profile",
      },
    ],
    images: [
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20close%20up%20photograph%20of%20a%20single%20beeswax%20candle%20flame%20on%20a%20dark%20oak%20counter%20in%20a%20wine%20cellar%2C%20golden%20warm%20flame%20glow%2C%20soft%20out%20of%20focus%20aged%20brick%20wall%20behind%2C%20fine%20film%20grain%2C%20intimate%20mood%2C%20editorial%20photography&width=1200&height=1500&seq=amber-cellar-img1&orientation=portrait",
        alt: "A single candle flame",
        caption: "The room's base layer · 1800K",
        aspect: "portrait",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20wide%20photograph%20of%20a%20Copenhagen%20underground%20wine%20cellar%20bar%20full%20of%20guests%20softly%20lit%20by%20candles%20and%20hidden%20amber%20uplight%20on%20the%20brick%20vault%2C%20warm%20honeyed%20atmosphere%2C%20quiet%20intimate%20conversations%2C%20fine%20film%20grain%2C%20cinematic%20hospitality%20photography&width=2000&height=1200&seq=amber-cellar-img2&orientation=landscape",
        alt: "Full cellar in service",
        caption: "Full house · Friday, 22:40",
        aspect: "landscape",
      },
    ],
    credits: [
      { role: "Lighting Design", name: "Elin Vahr Studio" },
      { role: "Client", name: "Bar Sølv" },
      { role: "Interior", name: "Studio Fjeld" },
      { role: "Photography", name: "Mikkel Hjuler" },
    ],
    pullquote: {
      quote: "The best light in a room is often the one no one can point at.",
      attribution: "Elin Vahr",
    },
  },
  {
    id: 3,
    slug: "chapel-of-dust",
    label: "03 / Residential",
    subtitle: "Casa Halo · Lisbon · 2024",
    title: "Chapel of Dust",
    location: "Lisbon, Portugal",
    year: "2024",
    discipline: "Residential · Living Room",
    tagline: "A living room designed to be lit by nothing but afternoon dust.",
    image:
      "https://readdy.ai/api/search-image?query=Soft%20lavender%20and%20cream%20light%20beams%20filtering%20through%20tall%20arched%20windows%20into%20a%20minimalist%20Portuguese%20chapel%20like%20living%20room%2C%20floating%20dust%20particles%20caught%20in%20the%20light%2C%20sculptural%20floor%20lamp%20on%20polished%20stone%20floor%2C%20cinematic%20editorial%20residential%20photography%2C%20calm%20negative%20space%20with%20fine%20grain&width=900&height=1200&seq=lumen-gallery-03&orientation=portrait",
    heroImage:
      "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20editorial%20photograph%20of%20a%20tall%20minimalist%20Portuguese%20chapel%20like%20living%20room%20in%20Lisbon%20with%20three%20towering%20arched%20windows%20casting%20soft%20lavender%20and%20cream%20light%20beams%20across%20a%20polished%20limestone%20floor%2C%20millions%20of%20fine%20dust%20particles%20suspended%20and%20glowing%20in%20the%20light%20shafts%2C%20a%20single%20sculptural%20oak%20floor%20lamp%20standing%20quietly%20in%20the%20corner%2C%20fine%20film%20grain%2C%20atmospheric%20haze%2C%20refined%20residential%20photography&width=2400&height=1350&seq=chapel-dust-hero&orientation=landscape",
    intro:
      "The owners asked for a room where nothing had to be turned on until well after dark. We designed the artificial lighting to be a supporting act — three quiet fixtures that only speak when the sun has finished. The rest of the day, the room lights itself.",
    chapters: [
      {
        eyebrow: "Chapter 01 · The Architecture",
        heading: "Three tall windows, no curtains.",
        body: "The room's daylight strategy is not lighting — it's architecture. Three 4.5m arched windows face south-west. The plaster is limewashed with 6% titanium dioxide so the walls bounce warm cream light back into the room without going yellow.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20wide%20interior%20photograph%20of%20three%20tall%20arched%20limewashed%20plaster%20windows%20in%20a%20Portuguese%20minimalist%20living%20room%2C%20soft%20cream%20afternoon%20light%20flooding%20a%20polished%20limestone%20floor%2C%20negative%20space%2C%20fine%20film%20grain%2C%20cinematic%20architectural%20photography&width=1800&height=1200&seq=chapel-dust-ch1&orientation=landscape",
        imageAlt: "Three tall windows, no curtains",
        alignment: "left",
      },
      {
        eyebrow: "Chapter 02 · The Long Wait",
        heading: "The room is designed to be empty.",
        body: "For most of the afternoon, nothing here is switched on. Nothing needs to be. The single sculptural floor lamp, hand-turned oak with a linen shade, holds its position for later — as a promise, not an object.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20a%20single%20tall%20sculptural%20hand%20turned%20oak%20floor%20lamp%20with%20a%20natural%20linen%20shade%20standing%20quietly%20unlit%20in%20a%20sunlit%20corner%20of%20a%20minimalist%20Portuguese%20living%20room%2C%20polished%20limestone%20floor%2C%20warm%20afternoon%20light%2C%20fine%20film%20grain%2C%20cinematic%20mood&width=1800&height=1200&seq=chapel-dust-ch2&orientation=landscape",
        imageAlt: "The floor lamp, unlit, waiting",
        alignment: "right",
      },
      {
        eyebrow: "Chapter 03 · Twenty Minutes After Sunset",
        heading: "The room breathes in.",
        body: "Only when the sun has fully set do the three fixtures come alive — the floor lamp at 2400K, a hidden cove behind the ceiling beam at 2200K, and a small reading sconce by the armchair at 2600K. Together they form what the family calls 'the second daylight.'",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20photograph%20of%20a%20minimalist%20Portuguese%20living%20room%20at%20blue%20hour%20softly%20lit%20by%20a%20warm%20oak%20floor%20lamp%20and%20a%20hidden%20amber%20cove%20behind%20a%20ceiling%20beam%2C%20polished%20limestone%20floor%20glowing%20gently%2C%20quiet%20intimate%20mood%2C%20fine%20film%20grain%2C%20refined%20warm%20palette&width=1800&height=1200&seq=chapel-dust-ch3&orientation=landscape",
        imageAlt: "The room at blue hour",
        alignment: "left",
      },
    ],
    techniques: [
      {
        code: "T-01",
        title: "Titanium-tuned plaster",
        detail: "Walls limewashed with 6% titanium dioxide to bounce daylight warm instead of yellow.",
        spec: "6% TiO₂ · limewash · 3 coats",
      },
      {
        code: "T-02",
        title: "Beam cove",
        detail: "A concealed linear cove runs the length of the central beam, on only after sunset.",
        spec: "2200K · 4W/m · astronomical timer",
      },
      {
        code: "T-03",
        title: "Sculptural floor lamp",
        detail: "Hand-turned oak, natural linen shade, single 2400K bulb with dim-to-warm behaviour.",
        spec: "2400K → 1800K on dim",
      },
    ],
    images: [
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20millions%20of%20dust%20particles%20caught%20in%20a%20soft%20cream%20afternoon%20light%20beam%20inside%20a%20minimalist%20Portuguese%20living%20room%20with%20tall%20arched%20windows%20and%20polished%20limestone%20floor%2C%20fine%20film%20grain%2C%20cinematic%20atmospheric%20mood&width=2000&height=1200&seq=chapel-dust-img1&orientation=landscape",
        alt: "Dust in the afternoon light",
        caption: "Afternoon dust · 16:40",
        aspect: "landscape",
      },
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20close%20up%20photograph%20of%20a%20warm%20oak%20sculptural%20floor%20lamp%20softly%20lit%20at%20dusk%20in%20a%20quiet%20Portuguese%20living%20room%2C%20natural%20linen%20shade%20glowing%2C%20fine%20film%20grain%2C%20refined%20warm%20palette%2C%20cinematic%20editorial%20photography&width=1200&height=1500&seq=chapel-dust-img2&orientation=portrait",
        alt: "The floor lamp at dusk",
        caption: "The lamp finally speaks",
        aspect: "portrait",
      },
    ],
    credits: [
      { role: "Lighting Design", name: "Elin Vahr Studio" },
      { role: "Client", name: "Private — Casa Halo" },
      { role: "Architecture", name: "Atelier Silva" },
      { role: "Photography", name: "Rita Sequeira" },
    ],
    pullquote: {
      quote: "The best light I designed here is the one I didn't switch on.",
      attribution: "Elin Vahr",
    },
  },
  {
    id: 4,
    slug: "ember-table",
    label: "04 / Restaurant",
    subtitle: "Fjord · Oslo · 2022",
    title: "Ember Table",
    location: "Oslo, Norway",
    year: "2022",
    discipline: "Restaurant · Communal Dining",
    tagline: "A twelve-metre communal table, lit as if by a slow-burning fire directly overhead.",
    image:
      "https://readdy.ai/api/search-image?query=Long%20communal%20dining%20table%20glowing%20under%20a%20suspended%20line%20of%20warm%20ember%20lights%2C%20moody%20Scandinavian%20restaurant%20interior%20in%20soft%20peach%20and%20rust%20tones%2C%20dark%20walls%20fading%20to%20shadow%2C%20cinematic%20editorial%20hospitality%20photography%20with%20harmonious%20warm%20palette%2C%20negative%20space%20and%20subtle%20film%20grain&width=900&height=1200&seq=lumen-gallery-04&orientation=portrait",
    heroImage:
      "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20photograph%20of%20a%20twelve%20metre%20long%20oak%20communal%20dining%20table%20in%20an%20Oslo%20restaurant%2C%20a%20single%20continuous%20line%20of%20warm%20ember%20amber%20pendants%20suspended%20low%20above%20the%20table%20length%2C%20guests%20in%20quiet%20conversation%20along%20both%20sides%2C%20dark%20plaster%20walls%20fading%20into%20shadow%2C%20honey%20warm%20glow%20on%20plates%20and%20hands%2C%20fine%20film%20grain%2C%20refined%20editorial%20hospitality%20photography%20by%20a%20master%20architectural%20photographer&width=2400&height=1350&seq=ember-table-hero&orientation=landscape",
    intro:
      "Fjord serves one seating a night at one table. We were asked to make the table itself feel like the hearth of the room — glowing, warm, unmoving — while everything else recedes into the Norwegian dark.",
    chapters: [
      {
        eyebrow: "Chapter 01 · The Ember Line",
        heading: "One long light for one long table.",
        body: "A single continuous track carries eighteen ember-toned pendants at 74cm above the table surface — low enough that diners duck slightly when standing. Each pendant is individually dimmable and warms further as the evening progresses.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20eighteen%20warm%20ember%20amber%20pendant%20lights%20suspended%20in%20a%20continuous%20line%20above%20a%20long%20oak%20communal%20dining%20table%2C%20soft%20honeyed%20glow%20on%20plates%20and%20wine%20glasses%2C%20dark%20plaster%20walls%20behind%2C%20fine%20film%20grain%2C%20cinematic%20editorial%20restaurant%20photography&width=1800&height=1200&seq=ember-table-ch1&orientation=landscape",
        imageAlt: "The ember line above the table",
        alignment: "left",
      },
      {
        eyebrow: "Chapter 02 · Dim As It Cooks",
        heading: "The light slows with the meal.",
        body: "The pendants are on a five-hour astronomical curve — starting at 2600K when guests arrive, then drifting to 1900K by dessert. Nobody notices. Everybody feels it.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20a%20restaurant%20table%20late%20in%20service%20with%20dessert%20plates%2C%20candles%20and%20almost%20ember%20orange%20warm%20pendant%20light%20above%2C%20guests%20leaning%20in%2C%20intimate%20mood%2C%20fine%20film%20grain%2C%20cinematic%20hospitality%20photography&width=1800&height=1200&seq=ember-table-ch2&orientation=landscape",
        imageAlt: "Late service — warmed further",
        alignment: "right",
      },
    ],
    techniques: [
      {
        code: "T-01",
        title: "Ember pendant line",
        detail: "18 hand-blown smoked glass pendants on a single 12m brass track above the table.",
        spec: "2600K → 1900K · 5h curve · DALI",
      },
      {
        code: "T-02",
        title: "Perimeter dark",
        detail: "The rest of the room sits below 30 lux, tuned so the table reads as the only bright surface.",
        spec: "≤30 lux · 2700K",
      },
    ],
    images: [
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20wide%20cinematic%20photograph%20of%20a%20full%20Oslo%20restaurant%20room%20with%20a%20single%20glowing%20communal%20oak%20table%20at%20the%20centre%2C%20warm%20ember%20pendants%20above%2C%20dark%20plaster%20walls%20around%2C%20guests%20softly%20lit%2C%20fine%20film%20grain%2C%20refined%20mood&width=2000&height=1200&seq=ember-table-img1&orientation=landscape",
        alt: "The full room in service",
        caption: "Full seating · 20:15",
        aspect: "landscape",
      },
    ],
    credits: [
      { role: "Lighting Design", name: "Elin Vahr Studio" },
      { role: "Client", name: "Fjord Restaurant" },
      { role: "Interior", name: "Kolstad Arkitekter" },
      { role: "Photography", name: "Ola Bergmann" },
    ],
    pullquote: {
      quote: "A shared table only works if the light says everyone is welcome and no one is being watched.",
      attribution: "Elin Vahr",
    },
  },
  {
    id: 5,
    slug: "slow-weather",
    label: "05 / Installation",
    subtitle: "Cloudroom · Marfa · 2025",
    title: "Slow Weather",
    location: "Marfa, Texas",
    year: "2025",
    discipline: "Installation · Immersive Room",
    tagline: "An indoor sky that moves through a full weather system every forty-two minutes.",
    image:
      "https://readdy.ai/api/search-image?query=Immersive%20gallery%20installation%20of%20soft%20glowing%20pastel%20mint%20and%20sky%20blue%20light%20clouds%20hovering%20inside%20a%20dark%20minimalist%20white%20cube%20room%2C%20single%20silhouetted%20visitor%20standing%20in%20awe%2C%20cinematic%20editorial%20art%20installation%20photography%20with%20dreamy%20negative%20space%20and%20gentle%20grain&width=900&height=1200&seq=lumen-gallery-05&orientation=portrait",
    heroImage:
      "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20photograph%20of%20an%20immersive%20light%20installation%20in%20a%20Marfa%20Texas%20gallery%2C%20soft%20pastel%20mint%20and%20sky%20blue%20glowing%20clouds%20hovering%20inside%20a%20vast%20white%20cube%20room%2C%20a%20lone%20silhouetted%20visitor%20standing%20in%20awe%20at%20the%20centre%2C%20polished%20concrete%20floor%20reflecting%20the%20soft%20luminous%20colours%2C%20quiet%20dreamlike%20atmosphere%2C%20fine%20film%20grain%2C%20refined%20editorial%20art%20photography&width=2400&height=1350&seq=slow-weather-hero&orientation=landscape",
    intro:
      "Slow Weather is a forty-two-minute cycle: dawn, high noon, storm, dusk, night. All of it happens indoors, without projections. The room becomes weather.",
    chapters: [
      {
        eyebrow: "Chapter 01 · The Cycle",
        heading: "A day compressed into forty-two minutes.",
        body: "Six hundred and eighty individually addressed LED nodes ride a smooth colour-mixing engine tuned to the exact spectral curves of a west Texas sky between April and October. Nothing loops the same way twice.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20a%20vast%20white%20cube%20gallery%20room%20filled%20with%20a%20soft%20pastel%20orange%20and%20pink%20light%20cloud%20at%20simulated%20dawn%2C%20polished%20concrete%20floor%20reflecting%20the%20glow%2C%20quiet%20lone%20visitor%20silhouette%2C%20fine%20film%20grain%2C%20cinematic%20art%20installation%20photography&width=1800&height=1200&seq=slow-weather-ch1&orientation=landscape",
        imageAlt: "The room at simulated dawn",
        alignment: "left",
      },
      {
        eyebrow: "Chapter 02 · The Storm",
        heading: "Weather without water.",
        body: "For eleven minutes of every cycle, the room enters a simulated thunderstorm — deep grey light, five discreet white flashes at unpredictable intervals, and a slow return to violet. Most visitors don't leave until it's over.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20photograph%20of%20a%20vast%20gallery%20room%20filled%20with%20deep%20grey%20and%20violet%20storm%20light%2C%20a%20lone%20silhouetted%20visitor%20standing%20still%2C%20polished%20concrete%20floor%2C%20quiet%20dramatic%20mood%2C%20fine%20film%20grain%2C%20refined%20art%20photography&width=1800&height=1200&seq=slow-weather-ch2&orientation=landscape",
        imageAlt: "The room during simulated storm",
        alignment: "right",
      },
    ],
    techniques: [
      {
        code: "T-01",
        title: "Spectral sky engine",
        detail: "680 addressable RGBW nodes running a live spectral simulation of west Texas atmosphere.",
        spec: "42 min cycle · never loops identically",
      },
      {
        code: "T-02",
        title: "Storm flash choreography",
        detail: "Five white-flash events per cycle, timed by a pseudo-random seed for each visitor session.",
        spec: "5000K · 60ms · 5 per cycle",
      },
    ],
    images: [
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20wide%20photograph%20of%20a%20vast%20white%20gallery%20room%20softly%20filled%20with%20pastel%20violet%20and%20deep%20blue%20simulated%20night%20light%2C%20a%20small%20group%20of%20silhouetted%20visitors%20sitting%20on%20the%20polished%20concrete%20floor%2C%20fine%20film%20grain%2C%20cinematic%20mood&width=2000&height=1200&seq=slow-weather-img1&orientation=landscape",
        alt: "Simulated night, visitors seated",
        caption: "Night phase · minute 37",
        aspect: "landscape",
      },
    ],
    credits: [
      { role: "Lighting Design", name: "Elin Vahr Studio" },
      { role: "Client", name: "Cloudroom Foundation" },
      { role: "Software", name: "Ferrell/Kim" },
      { role: "Photography", name: "Danny Ochoa" },
    ],
    pullquote: {
      quote: "We asked people to sit inside a sky. Almost everyone stayed for two cycles.",
      attribution: "Elin Vahr",
    },
  },
  {
    id: 6,
    slug: "the-long-golden-hour",
    label: "06 / Residential",
    subtitle: "Villa Twilight · Ibiza · 2023",
    title: "The Long Golden Hour",
    location: "Ibiza, Spain",
    year: "2023",
    discipline: "Residential · Villa",
    tagline: "A house lit to hold the last twenty minutes of Mediterranean sun — long after the sun is gone.",
    image:
      "https://readdy.ai/api/search-image?query=Warm%20coral%20and%20peach%20sunset%20light%20spilling%20across%20a%20minimalist%20white%20plaster%20villa%20terrace%20at%20dusk%2C%20sculptural%20sconce%20casting%20a%20soft%20glow%20on%20textured%20wall%2C%20quiet%20infinity%20pool%20reflection%2C%20cinematic%20editorial%20residential%20photography%20with%20harmonious%20pastel%20warm%20tones%20and%20calm%20negative%20space%2C%20subtle%20film%20grain&width=900&height=1200&seq=lumen-gallery-06&orientation=portrait",
    heroImage:
      "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20editorial%20photograph%20of%20a%20minimalist%20white%20plaster%20Ibiza%20villa%20terrace%20at%20blue%20hour%20softly%20glowing%20with%20warm%20coral%20and%20peach%20artificial%20light%20as%20if%20holding%20the%20last%20minutes%20of%20sunset%2C%20quiet%20infinity%20pool%20reflecting%20the%20glow%2C%20sculptural%20wall%20sconce%20casting%20a%20soft%20wash%20on%20the%20textured%20plaster%2C%20fine%20film%20grain%2C%20atmospheric%20haze%2C%20refined%20residential%20photography&width=2400&height=1350&seq=long-golden-hero&orientation=landscape",
    intro:
      "The owners wanted to sit outside for hours after sunset without ever feeling that the day had ended. The entire exterior scheme is a slow, warm illusion — the villa itself remembers the sun.",
    chapters: [
      {
        eyebrow: "Chapter 01 · The Plaster Warmth",
        heading: "The wall becomes the light.",
        body: "Every terrace wall is washed by a hidden 2000K coral-tuned LED, angled to catch the plaster's texture. From ten metres away, the wall appears to still be glowing from the sun that left an hour ago.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20a%20textured%20white%20plaster%20villa%20wall%20washed%20by%20a%20hidden%20warm%20coral%20LED%20light%20at%20blue%20hour%2C%20wall%20surface%20glowing%20softly%20as%20if%20holding%20the%20last%20sunset%2C%20fine%20film%20grain%2C%20cinematic%20architectural%20photography&width=1800&height=1200&seq=long-golden-ch1&orientation=landscape",
        imageAlt: "The wall remembers the sun",
        alignment: "left",
      },
    ],
    techniques: [
      {
        code: "T-01",
        title: "Coral wall wash",
        detail: "Hidden linear LEDs graze every plaster surface at 2000K coral-tuned white.",
        spec: "2000K coral · 5W/m · grazing angle",
      },
      {
        code: "T-02",
        title: "Pool underlight",
        detail: "A single warm underwater channel keeps the pool visibly present without turning it into a beacon.",
        spec: "2400K · 3W · IP68",
      },
    ],
    images: [
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20wide%20photograph%20of%20a%20minimalist%20Ibiza%20villa%20terrace%20at%20late%20blue%20hour%20with%20warm%20coral%20wall%20washes%20glowing%20softly%2C%20quiet%20infinity%20pool%20reflection%2C%20one%20small%20lounge%20chair%2C%20fine%20film%20grain%2C%20cinematic%20mood&width=2000&height=1200&seq=long-golden-img1&orientation=landscape",
        alt: "The full terrace at blue hour",
        caption: "Terrace · 21:40",
        aspect: "landscape",
      },
    ],
    credits: [
      { role: "Lighting Design", name: "Elin Vahr Studio" },
      { role: "Client", name: "Private — Villa Twilight" },
      { role: "Architecture", name: "Estudio Balear" },
      { role: "Photography", name: "Marta Ríos" },
    ],
    pullquote: {
      quote: "We didn't extend the day. We taught the walls to remember it.",
      attribution: "Elin Vahr",
    },
  },
  {
    id: 7,
    slug: "velvet-threshold",
    label: "07 / Retail",
    subtitle: "Maison Noor · Paris · 2024",
    title: "Velvet Threshold",
    location: "Paris, France",
    year: "2024",
    discipline: "Retail · Boutique",
    tagline: "A boutique where the merchandise is lit brighter than the customer — a rare, precise inversion.",
    image:
      "https://readdy.ai/api/search-image?query=Softly%20glowing%20boutique%20interior%20with%20sculptural%20halo%20pendant%20hovering%20above%20a%20dark%20velvet%20display%20plinth%2C%20warm%20champagne%20and%20rose%20light%20bouncing%20off%20matte%20plaster%20walls%2C%20quiet%20luxury%20retail%20cinematic%20editorial%20photography%20with%20harmonious%20warm%20palette%2C%20deep%20negative%20space%20and%20gentle%20film%20grain&width=900&height=1200&seq=lumen-gallery-07&orientation=portrait",
    heroImage:
      "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20editorial%20photograph%20of%20a%20quiet%20luxury%20Paris%20boutique%20interior%20with%20a%20sculptural%20halo%20pendant%20hovering%20above%20a%20dark%20velvet%20display%20plinth%20holding%20a%20single%20jewellery%20piece%2C%20warm%20champagne%20and%20rose%20light%20softly%20bouncing%20off%20matte%20plaster%20walls%2C%20polished%20dark%20timber%20floor%2C%20fine%20film%20grain%2C%20atmospheric%20haze%2C%20deep%20negative%20space%2C%20refined%20retail%20photography&width=2400&height=1350&seq=velvet-threshold-hero&orientation=landscape",
    intro:
      "Maison Noor sells one piece at a time. The room is lit accordingly — the object glows, and the space around it recedes almost to nothing.",
    chapters: [
      {
        eyebrow: "Chapter 01 · The Halo",
        heading: "One pendant per plinth.",
        body: "Each display plinth carries a bespoke halo pendant hand-machined from anodised aluminium. The beam is tight — 8° — and warm — 2600K — landing exactly on the object and nothing else.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20close%20up%20photograph%20of%20a%20sculptural%20halo%20pendant%20casting%20a%20precise%20warm%20champagne%20light%20on%20a%20single%20piece%20of%20jewellery%20resting%20on%20a%20dark%20velvet%20plinth%2C%20blurred%20matte%20plaster%20background%2C%20fine%20film%20grain%2C%20cinematic%20luxury%20retail%20photography&width=1800&height=1200&seq=velvet-threshold-ch1&orientation=landscape",
        imageAlt: "The halo pendant and its object",
        alignment: "left",
      },
    ],
    techniques: [
      {
        code: "T-01",
        title: "8° halo pendants",
        detail: "Custom anodised aluminium halos, tight beam, hung 62cm above each plinth.",
        spec: "2600K · 8° beam · CRI 98",
      },
      {
        code: "T-02",
        title: "Bounce-only ambient",
        detail: "No overhead light in the room. Everything the customer sees is reflected off the plaster.",
        spec: "0 direct · bounced only",
      },
    ],
    images: [
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20wide%20photograph%20of%20a%20quiet%20luxury%20Paris%20boutique%20with%20three%20softly%20glowing%20display%20plinths%20each%20with%20its%20own%20halo%20pendant%2C%20polished%20dark%20timber%20floor%2C%20matte%20plaster%20walls%2C%20fine%20film%20grain%2C%20refined%20cinematic%20mood&width=2000&height=1200&seq=velvet-threshold-img1&orientation=landscape",
        alt: "The full boutique",
        caption: "Full room · one visitor",
        aspect: "landscape",
      },
    ],
    credits: [
      { role: "Lighting Design", name: "Elin Vahr Studio" },
      { role: "Client", name: "Maison Noor" },
      { role: "Interior", name: "Atelier Pichet" },
      { role: "Photography", name: "Camille Marchand" },
    ],
    pullquote: {
      quote: "The object glows. The person choosing it should feel almost invisible. That's respect.",
      attribution: "Elin Vahr",
    },
  },
  {
    id: 8,
    slug: "whisper-wing",
    label: "08 / Cultural",
    subtitle: "Kunsthalle Nord · Stockholm · 2022",
    title: "Whisper Wing",
    location: "Stockholm, Sweden",
    year: "2022",
    discipline: "Cultural · Museum Corridor",
    tagline: "A museum corridor lit at half the recommended lux — and every visitor walks slower for it.",
    image:
      "https://readdy.ai/api/search-image?query=Museum%20corridor%20with%20a%20soft%20wash%20of%20pale%20mint%20and%20cream%20light%20grazing%20tall%20plaster%20walls%2C%20a%20single%20silhouetted%20visitor%20walking%20through%20the%20beam%2C%20minimalist%20Nordic%20architecture%20cinematic%20editorial%20photography%20with%20quiet%20negative%20space%2C%20harmonious%20cool%20pastel%20palette%20and%20subtle%20film%20grain&width=900&height=1200&seq=lumen-gallery-08&orientation=portrait",
    heroImage:
      "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20photograph%20of%20a%20long%20minimalist%20Nordic%20museum%20corridor%20softly%20lit%20by%20a%20wash%20of%20pale%20mint%20and%20cream%20light%20grazing%20tall%20plaster%20walls%2C%20a%20single%20silhouetted%20visitor%20walking%20slowly%20through%20the%20centre%20of%20the%20frame%2C%20polished%20light%20concrete%20floor%20reflecting%20soft%20highlights%2C%20quiet%20negative%20space%2C%20fine%20film%20grain%2C%20refined%20editorial%20museum%20photography&width=2400&height=1350&seq=whisper-wing-hero&orientation=landscape",
    intro:
      "The Kunsthalle asked for a corridor that would slow people down between two loud galleries. We lit it at half the recommended lux and let the walls do the talking.",
    chapters: [
      {
        eyebrow: "Chapter 01 · The Half-Lux",
        heading: "Less light, more attention.",
        body: "Ambient sits at 80 lux — half of the museum guideline. Instead of feeling dark, the corridor feels quiet. Visitors instinctively drop their voices.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20a%20quiet%20Nordic%20museum%20corridor%20softly%20lit%20with%20pale%20mint%20wall%20grazing%20light%20and%20polished%20light%20concrete%20floor%2C%20a%20single%20visitor%20standing%20still%20mid%20corridor%2C%20fine%20film%20grain%2C%20cinematic%20architectural%20photography&width=1800&height=1200&seq=whisper-wing-ch1&orientation=landscape",
        imageAlt: "The corridor at half-lux",
        alignment: "left",
      },
    ],
    techniques: [
      {
        code: "T-01",
        title: "Wall-grazing pastel",
        detail: "Linear LEDs at 15cm from the plaster wall, tuned to 3200K with a slight mint tint.",
        spec: "3200K · +5% mint · 15cm setback",
      },
    ],
    images: [
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20wide%20photograph%20of%20a%20Nordic%20museum%20corridor%20with%20soft%20mint%20wall%20wash%20and%20a%20small%20group%20of%20visitors%20quietly%20walking%20through%2C%20fine%20film%20grain%2C%20refined%20cinematic%20mood&width=2000&height=1200&seq=whisper-wing-img1&orientation=landscape",
        alt: "Visitors in the corridor",
        caption: "Weekday morning · 10:20",
        aspect: "landscape",
      },
    ],
    credits: [
      { role: "Lighting Design", name: "Elin Vahr Studio" },
      { role: "Client", name: "Kunsthalle Nord" },
      { role: "Architecture", name: "Sten & Söner" },
      { role: "Photography", name: "Astrid Lindholm" },
    ],
    pullquote: {
      quote: "If you want people to pay attention, take a little light away, not add it.",
      attribution: "Elin Vahr",
    },
  },
  {
    id: 9,
    slug: "snowlight-bath",
    label: "09 / Hospitality",
    subtitle: "Ryokan Aomori · Aomori · 2025",
    title: "Snowlight Bath",
    location: "Aomori, Japan",
    year: "2025",
    discipline: "Hospitality · Onsen",
    tagline: "A traditional onsen lit by candlelight, snow reflection, and one paper lantern per bather.",
    image:
      "https://readdy.ai/api/search-image?query=Traditional%20Japanese%20onsen%20bath%20house%20at%20dusk%20lit%20by%20soft%20peach%20and%20lavender%20ambient%20light%2C%20steam%20drifting%20over%20dark%20stone%20pool%2C%20paper%20lanterns%20glowing%20gently%20along%20timber%20walkway%2C%20cinematic%20editorial%20hospitality%20interior%20photography%20with%20harmonious%20warm%20and%20cool%20pastel%20tones%2C%20deep%20negative%20space%20and%20subtle%20film%20grain&width=900&height=1200&seq=lumen-gallery-09&orientation=portrait",
    heroImage:
      "https://readdy.ai/api/search-image?query=Ultra%20realistic%20cinematic%20editorial%20photograph%20of%20a%20traditional%20Japanese%20onsen%20bathhouse%20in%20Aomori%20at%20dusk%2C%20warm%20candlelight%20and%20soft%20lavender%20snowlight%20mixing%20over%20a%20dark%20stone%20pool%20with%20drifting%20steam%2C%20paper%20lanterns%20glowing%20gently%20along%20a%20polished%20timber%20walkway%2C%20snow%20visible%20outside%20through%20a%20low%20opening%2C%20fine%20film%20grain%2C%20quiet%20intimate%20mood%2C%20refined%20hospitality%20photography%20by%20a%20master%20architectural%20photographer&width=2400&height=1350&seq=snowlight-bath-hero&orientation=landscape",
    intro:
      "The Aomori ryokan wanted an onsen that felt like it was being lit by the snow outside. We took the outdoor light in, warmed it, and gave every bather their own paper lantern.",
    chapters: [
      {
        eyebrow: "Chapter 01 · Snow As A Fixture",
        heading: "The outside becomes part of the palette.",
        body: "The bath opens to a slot of window along the water line, framing a snow bank that reflects daylight straight into the water. In winter it is, quite literally, the room's largest light source.",
        image:
          "https://readdy.ai/api/search-image?query=Ultra%20realistic%20photograph%20of%20a%20traditional%20Japanese%20onsen%20pool%20interior%20with%20a%20low%20slot%20window%20framing%20a%20soft%20lavender%20snow%20bank%20outside%2C%20snowlight%20reflecting%20onto%20the%20water%20surface%2C%20steam%20drifting%2C%20dark%20stone%20walls%2C%20fine%20film%20grain%2C%20refined%20cinematic%20mood&width=1800&height=1200&seq=snowlight-bath-ch1&orientation=landscape",
        imageAlt: "The slot window and the snow beyond",
        alignment: "left",
      },
    ],
    techniques: [
      {
        code: "T-01",
        title: "One lantern per bather",
        detail: "Handmade washi paper lanterns line the timber walkway, one placed by staff per guest.",
        spec: "2200K · single flame LED · washi",
      },
      {
        code: "T-02",
        title: "Snowlight aperture",
        detail: "A long horizontal slot window brings snow-reflected daylight straight to the water surface.",
        spec: "Cold daylight · bounced from snow",
      },
    ],
    images: [
      {
        src: "https://readdy.ai/api/search-image?query=Ultra%20realistic%20wide%20photograph%20of%20a%20traditional%20Japanese%20onsen%20bathhouse%20at%20blue%20hour%20with%20a%20line%20of%20warm%20paper%20lanterns%20along%20a%20timber%20walkway%2C%20drifting%20steam%20over%20dark%20stone%20pool%2C%20quiet%20intimate%20mood%2C%20fine%20film%20grain%2C%20cinematic%20mood&width=2000&height=1200&seq=snowlight-bath-img1&orientation=landscape",
        alt: "The walkway and pool at blue hour",
        caption: "Blue hour · empty bath",
        aspect: "landscape",
      },
    ],
    credits: [
      { role: "Lighting Design", name: "Elin Vahr Studio" },
      { role: "Client", name: "Ryokan Aomori" },
      { role: "Architecture", name: "Kaneda + Co." },
      { role: "Photography", name: "Sora Hayashi" },
    ],
    pullquote: {
      quote: "The snow was already there, doing the work. We just widened the window.",
      attribution: "Elin Vahr",
    },
  },
];